import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import CourseChips from './parts/CourseChips';
import ViewStudyMaterialModal from './parts/ViewStudyMaterialModal';

/* ─── Design Tokens ─────────────────────────────────────────────────── */
const C = {
  navy: '#0e225d', navyMid: '#1a3370',
  gold: '#fbb21f', goldDim: 'rgba(251,178,31,0.14)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', border: '#e5e7eb', muted: '#9ca3af', sub: '#6b7280', body: '#1f2937',
  green: '#15803d', greenDim: 'rgba(22,163,74,0.1)', greenBorder: 'rgba(22,163,74,0.25)',
};

/* ─── Types ──────────────────────────────────────────────────────────── */
export interface StudyMaterialVideo {
  video_id: string;
  original_name: string;
  status: string;
  m3u8_url: string;
  size: number;
}

export interface StudyMaterialFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

export interface StudyMaterial {
  id: string;
  chapter_id: string;
  module_id: string;
  course_id: string;
  institute_id: string;
  title: string;
  body: string;
  order: number;
  status: string;
  is_course_module_active: boolean;
  files: StudyMaterialFile[];
  video_links: string[];       // legacy — may be empty
  videos: StudyMaterialVideo[]; // new — HLS videos
  videos_count: number;
  files_count: number;
  links_count: number;
  created_at: string;
  created_by: string;
}

interface Chapter {
  id: string;
  module_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
  study_materials?: StudyMaterial[];
  materials_count: number;
}

interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
  chapters: Chapter[];
  chapters_count: number;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

const StudyMaterials: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [togglingModuleId, setTogglingModuleId] = useState<string | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [chapterMaterials, setChapterMaterials] = useState<{ [key: string]: StudyMaterial[] }>({});
  const [loadingChapterMaterials, setLoadingChapterMaterials] = useState<Set<string>>(new Set());

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  useEffect(() => {
    if (selectedCourseId && profile?.institute_id) fetchCourseStructure();
    else setModules([]);
  }, [selectedCourseId, profile]);

  const fetchCourseStructure = async () => {
    if (!profile?.institute_id) return;
    try {
      setLoading(true); setError(null);
      const response = await apiClient.get(`/v1/study-materials/course/${selectedCourseId}/structure`);
      if (response.data.success) {
        setModules(response.data.data.modules || []);
        if (response.data.data.modules?.length > 0) {
          const firstModule = response.data.data.modules[0];
          setExpandedModules(new Set([firstModule.id]));
          if (firstModule.chapters?.length > 0) setExpandedChapters(new Set([firstModule.chapters[0].id]));
        }
      } else { setError('Failed to load course structure'); }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load course structure');
    } finally { setLoading(false); }
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
  };

  const handleViewDetails = (material: StudyMaterial) => {
    setSelectedMaterial(material);
    setShowDetailsModal(true);
  };

  const handleToggleModule = async (materialId: string, currentStatus: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    setTogglingModuleId(materialId);
    try {
      const response = await apiClient.patch(
        `/v1/study-materials/${materialId}/toggle-module`,
        { is_course_module_active: !currentStatus }
      );
      if (response.data.success) {
        setChapterMaterials(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(chapterId => {
            newState[chapterId] = newState[chapterId].map(m =>
              m.id === materialId ? { ...m, is_course_module_active: !currentStatus } : m
            );
          });
          return newState;
        });
        toast.current?.show({ severity: 'success', summary: 'Success', detail: `Study material ${!currentStatus ? 'added to' : 'removed from'} course modules`, life: 3000 });
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: response.data.error || 'Failed to update module status', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to update module status', life: 4000 });
    } finally { setTogglingModuleId(null); }
  };

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules(prev => {
      const s = new Set(prev);
      s.has(moduleId) ? s.delete(moduleId) : s.add(moduleId);
      return s;
    });
  };

  const toggleChapterExpansion = async (chapterId: string) => {
    const isExpanded = expandedChapters.has(chapterId);
    setExpandedChapters(prev => {
      const s = new Set(prev);
      s.has(chapterId) ? s.delete(chapterId) : s.add(chapterId);
      return s;
    });
    if (!isExpanded && !chapterMaterials[chapterId]) await fetchChapterMaterials(chapterId);
  };

  const fetchChapterMaterials = async (chapterId: string) => {
    if (loadingChapterMaterials.has(chapterId)) return;
    setLoadingChapterMaterials(prev => { const s = new Set(prev); s.add(chapterId); return s; });
    try {
      const response = await apiClient.get(`/v1/study-materials/chapter/${chapterId}?status=active`);
      if (response.data.success) {
        setChapterMaterials(prev => ({ ...prev, [chapterId]: response.data.data || [] }));
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load study materials', life: 3000 });
    } finally {
      setLoadingChapterMaterials(prev => { const s = new Set(prev); s.delete(chapterId); return s; });
    }
  };

  const getTotalMaterialsCount = () =>
    modules.reduce((t, m) => t + m.chapters.reduce((ct, ch) => ct + (ch.materials_count || 0), 0), 0);

  const formatDate = (dateString: string) => {
    try { return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return dateString; }
  };

  if (profileLoading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
      <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
      <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile data…</p>
    </div>
  );

  if (!profile?.institute_id) return (
    <div style={{ padding: '20px' }}>
      <Message severity="warn" text="Unable to load profile data. Please refresh the page." style={{ fontSize: '0.88rem' }} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Toast ref={toast} />

      {/* ── Course Filter ─────────────────────────────────────────────────── */}
      <div style={{ background: C.white, borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
          <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <i className="pi pi-filter" style={{ fontSize: '0.75rem' }} /> Select Course
          </span>
          {selectedCourseId && (
            <button
              onClick={() => setSelectedCourseId('')}
              style={{ marginLeft: 'auto', padding: '5px 10px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '7px', color: C.sub, fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '5px' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EEF7'; e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.navy; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.sub; }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.68rem' }} /> Clear
            </button>
          )}
        </div>
        <CourseChips selectedCourseId={selectedCourseId} onCourseSelect={handleCourseSelect} showSearch={false} />
      </div>

      {error && <Message severity="error" text={error} style={{ fontSize: '0.88rem' }} />}

      {/* ── Empty / Loading / Content ─────────────────────────────────────── */}
      {!selectedCourseId ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
          <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <i className="pi pi-book" style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
          </div>
          <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>Select a Course</h3>
          <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>Click on a course above to view its study materials</p>
        </div>

      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
            <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading study materials…</p>
          </div>
        </div>

      ) : modules.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
          <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>No Study Materials Found</h3>
          <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>This course has no study materials yet</p>
        </div>

      ) : (
        <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy }}>Course Structure</span>
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: C.muted, fontWeight: '500' }}>
              {modules.length} module{modules.length !== 1 ? 's' : ''} · {getTotalMaterialsCount()} material{getTotalMaterialsCount() !== 1 ? 's' : ''}
            </span>
          </div>

          {modules.map((module) => {
            const isModuleExpanded = expandedModules.has(module.id);
            return (
              <div key={module.id} style={{ marginBottom: '10px' }}>

                {/* Module Header */}
                <div
                  onClick={() => toggleModuleExpansion(module.id)}
                  style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', borderRadius: isModuleExpanded ? '10px 10px 0 0' : '10px', cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative', overflow: 'hidden', boxShadow: '0 2px 6px rgba(14,34,93,0.18)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.26)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(14,34,93,0.18)'; }}
                >
                  <div style={{ position: 'absolute', top: '-16px', right: '-16px', width: '70px', height: '70px', background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(14px)', pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(251,178,31,0.2)', border: '1px solid rgba(251,178,31,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', fontWeight: '700', color: C.gold, flexShrink: 0 }}>
                      {module.order}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 2px 0', fontSize: '0.88rem', fontWeight: '700', color: C.white }}>{module.title}</h3>
                      {module.description && <p style={{ margin: 0, fontSize: '0.73rem', color: 'rgba(255,255,255,0.55)', lineHeight: '1.4' }}>{module.description}</p>}
                    </div>
                    <span style={{ fontSize: '0.68rem', color: C.gold, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <i className="pi pi-book" style={{ fontSize: '0.62rem' }} />
                      {module.chapters_count} {module.chapters_count === 1 ? 'Chapter' : 'Chapters'}
                    </span>
                    <i className={`pi ${isModuleExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ color: C.gold, fontSize: '0.8rem', flexShrink: 0 }} />
                  </div>
                </div>

                {/* Chapters */}
                {isModuleExpanded && (
                  <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '12px', background: 'rgba(14,34,93,0.015)' }}>
                    {module.chapters.map((chapter) => {
                      const isChapterExpanded = expandedChapters.has(chapter.id);
                      return (
                        <div key={chapter.id} style={{ marginBottom: '8px' }}>

                          {/* Chapter Header */}
                          <div
                            onClick={() => toggleChapterExpansion(chapter.id)}
                            style={{ padding: '10px 14px', background: isChapterExpanded ? '#E8EEF7' : C.white, border: `1px solid ${isChapterExpanded ? 'rgba(14,34,93,0.2)' : C.border}`, borderRadius: isChapterExpanded ? '8px 8px 0 0' : '8px', cursor: 'pointer', transition: 'all 0.15s ease' }}
                            onMouseEnter={(e) => { if (!isChapterExpanded) { e.currentTarget.style.background = '#F5F7FA'; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; } }}
                            onMouseLeave={(e) => { if (!isChapterExpanded) { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; } }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: isChapterExpanded ? 'rgba(14,34,93,0.12)' : '#F5F7FA', border: `1px solid ${isChapterExpanded ? 'rgba(14,34,93,0.2)' : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: '700', color: C.navy, flexShrink: 0 }}>
                                {module.order}.{chapter.order}
                              </div>
                              <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 1px 0', fontSize: '0.83rem', fontWeight: '600', color: C.navy }}>{chapter.title}</h4>
                                {chapter.description && <p style={{ margin: 0, fontSize: '0.71rem', color: C.sub }}>{chapter.description}</p>}
                              </div>
                              <span style={{ fontSize: '0.68rem', color: C.muted, display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                                <i className="pi pi-file" style={{ fontSize: '0.6rem' }} />
                                {chapter.materials_count} {chapter.materials_count === 1 ? 'Material' : 'Materials'}
                              </span>
                              <i className={`pi ${isChapterExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} style={{ color: C.navy, fontSize: '0.75rem', opacity: 0.5, flexShrink: 0 }} />
                            </div>
                          </div>

                          {/* Study Materials */}
                          {isChapterExpanded && (
                            <div style={{ border: `1px solid rgba(14,34,93,0.12)`, borderTop: 'none', borderRadius: '0 0 8px 8px', padding: '10px', background: C.white }}>
                              {loadingChapterMaterials.has(chapter.id) ? (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                  <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".8s" />
                                  <p style={{ margin: '8px 0 0 0', fontSize: '0.78rem', color: C.muted }}>Loading materials…</p>
                                </div>
                              ) : chapterMaterials[chapter.id]?.length > 0 ? (
                                chapterMaterials[chapter.id].map((material) => {
                                  const videoCount  = material.videos?.length || material.videos_count || 0;
                                  const fileCount   = material.files?.length || material.files_count || 0;
                                  const linkCount   = material.video_links?.length || material.links_count || 0;

                                  return (
                                    <div
                                      key={material.id}
                                      onClick={() => handleViewDetails(material)}
                                      style={{ padding: '12px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '9px', marginBottom: '7px', cursor: 'pointer', transition: 'all 0.15s ease' }}
                                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(3px)'; e.currentTarget.style.borderColor = C.goldBorder; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.07)'; }}
                                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        {/* Order badge */}
                                        <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 6px rgba(14,34,93,0.18)', fontSize: '0.55rem', fontWeight: '700', color: C.gold, letterSpacing: '-0.3px', lineHeight: 1 }}>
                                          {module.order}.{chapter.order}.{material.order ?? '?'}
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <h5 style={{ margin: '0 0 3px 0', fontSize: '0.83rem', fontWeight: '600', color: C.navy }}>{material.title}</h5>
                                          {material.body && material.body !== '.' && (
                                            <p style={{ margin: '0 0 7px 0', fontSize: '0.75rem', color: C.sub, lineHeight: '1.45', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                              {material.body}
                                            </p>
                                          )}

                                          {/* Meta row */}
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.67rem', color: C.muted }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                              <i className="pi pi-calendar" style={{ fontSize: '0.6rem' }} />
                                              {formatDate(material.created_at)}
                                            </span>
                                            {fileCount > 0 && (
                                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: C.navy, fontWeight: '600' }}>
                                                <i className="pi pi-paperclip" style={{ fontSize: '0.6rem' }} />
                                                {fileCount} {fileCount === 1 ? 'file' : 'files'}
                                              </span>
                                            )}
                                            {videoCount > 0 && (
                                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#b8860b', fontWeight: '600' }}>
                                                <i className="pi pi-video" style={{ fontSize: '0.6rem' }} />
                                                {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                                              </span>
                                            )}
                                            {linkCount > 0 && (
                                              <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: C.sub, fontWeight: '600' }}>
                                                <i className="pi pi-link" style={{ fontSize: '0.6rem' }} />
                                                {linkCount} {linkCount === 1 ? 'link' : 'links'}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {/* Toggle module button */}
                                        <button
                                          onClick={(e) => handleToggleModule(material.id, material.is_course_module_active ?? false, e)}
                                          disabled={togglingModuleId === material.id}
                                          title={material.is_course_module_active ? 'Remove from Course Modules' : 'Add to Course Modules'}
                                          style={{ padding: '5px 10px', background: material.is_course_module_active ? C.greenDim : 'rgba(14,34,93,0.06)', color: material.is_course_module_active ? C.green : C.navy, border: `1px solid ${material.is_course_module_active ? C.greenBorder : 'rgba(14,34,93,0.18)'}`, borderRadius: '7px', cursor: togglingModuleId === material.id ? 'not-allowed' : 'pointer', fontSize: '0.68rem', fontWeight: '700', transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, opacity: togglingModuleId === material.id ? 0.6 : 1 }}
                                          onMouseEnter={(e) => { if (togglingModuleId !== material.id) e.currentTarget.style.transform = 'scale(1.03)'; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                        >
                                          {togglingModuleId === material.id ? (
                                            <><i className="pi pi-spin pi-spinner" style={{ fontSize: '0.62rem' }} />Updating…</>
                                          ) : (
                                            <><i className={`pi ${material.is_course_module_active ? 'pi-check-circle' : 'pi-circle'}`} style={{ fontSize: '0.62rem' }} />{material.is_course_module_active ? 'Module' : 'Add'}</>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div style={{ padding: '20px', textAlign: 'center' }}>
                                  <div style={{ width: '38px', height: '38px', background: 'rgba(14,34,93,0.05)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                                    <i className="pi pi-inbox" style={{ fontSize: '1rem', color: C.navy, opacity: 0.25 }} />
                                  </div>
                                  <p style={{ margin: 0, fontSize: '0.78rem', color: C.muted }}>No study materials in this chapter</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Details Modal ─────────────────────────────────────────────────── */}
      <ViewStudyMaterialModal
        visible={showDetailsModal}
        material={selectedMaterial}
        onHide={() => setShowDetailsModal(false)}
      />
    </div>
  );
};

export default StudyMaterials;

// import React, { useState, useEffect, useRef } from 'react';
// import { Toast } from 'primereact/toast';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import { Message } from 'primereact/message';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
// import CourseChips from './parts/CourseChips';
// import ViewStudyMaterialModal from './parts/ViewStudyMaterialModal';

// /* ─── Design Tokens ────────────────────────────────────────────────── */
// const C = {
//   navy: '#0e225d',
//   navyMid: '#1a3370',
//   gold: '#fbb21f',
//   goldDim: 'rgba(251,178,31,0.14)',
//   goldBorder: 'rgba(251,178,31,0.35)',
//   white: '#ffffff',
//   border: '#e5e7eb',
//   muted: '#9ca3af',
//   sub: '#6b7280',
//   body: '#1f2937',
//   green: '#15803d',
//   greenDim: 'rgba(22,163,74,0.1)',
//   greenBorder: 'rgba(22,163,74,0.25)',
// };

// interface StudyMaterialFile {
//   file_name: string;
//   key: string;
//   url: string;
//   size: number;
//   mime_type: string;
//   uploaded_at: string;
// }

// interface StudyMaterial {
//   id: string;
//   chapter_id: string;
//   module_id: string;
//   course_id: string;
//   institute_id: string;
//   title: string;
//   body: string;
//   order: number;
//   status: string;
//   is_course_module_active: boolean;
//   files: StudyMaterialFile[];
//   video_links: string[];
//   files_count?: number;
//   videos_count?: number;
//   created_at: string;
//   created_by: string;
// }

// interface Chapter {
//   id: string;
//   module_id: string;
//   title: string;
//   description: string;
//   order: number;
//   status: string;
//   study_materials?: StudyMaterial[];
//   materials_count: number;
// }

// interface Module {
//   id: string;
//   course_id: string;
//   title: string;
//   description: string;
//   order: number;
//   status: string;
//   chapters: Chapter[];
//   chapters_count: number;
// }

// interface Course {
//   course_id: string;
//   name: string;
//   title: string;
//   status: string;
// }

// const StudyMaterials: React.FC = () => {
//   const toast = useRef<Toast>(null);
//   const [modules, setModules] = useState<Module[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [selectedCourseId, setSelectedCourseId] = useState<string>('');
//   const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);
//   const [showDetailsModal, setShowDetailsModal] = useState(false);
//   const [togglingModuleId, setTogglingModuleId] = useState<string | null>(null);
//   const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
//   const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
//   const [chapterMaterials, setChapterMaterials] = useState<{ [key: string]: StudyMaterial[] }>({});
//   const [loadingChapterMaterials, setLoadingChapterMaterials] = useState<Set<string>>(new Set());

//   const { profile, loading: profileLoading } = useOperatorProfileStore();

//   useEffect(() => {
//     if (selectedCourseId && profile?.institute_id) {
//       fetchCourseStructure();
//     } else {
//       setModules([]);
//     }
//   }, [selectedCourseId, profile]);

//   const fetchCourseStructure = async () => {
//     if (!profile?.institute_id) return;
//     try {
//       setLoading(true);
//       setError(null);
//       const response = await apiClient.get(`/v1/study-materials/course/${selectedCourseId}/structure`);
//       if (response.data.success) {
//         setModules(response.data.data.modules || []);
//         if (response.data.data.modules?.length > 0) {
//           const firstModule = response.data.data.modules[0];
//           setExpandedModules(new Set([firstModule.id]));
//           if (firstModule.chapters?.length > 0) {
//             setExpandedChapters(new Set([firstModule.chapters[0].id]));
//           }
//         }
//       } else { setError('Failed to load course structure'); }
//     } catch (err: any) {
//       setError(err.response?.data?.error || 'Failed to load course structure');
//     } finally { setLoading(false); }
//   };

//   const handleCourseSelect = (course: Course) => {
//     setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
//   };

//   const handleViewDetails = (material: StudyMaterial) => {
//     setSelectedMaterial(material);
//     setShowDetailsModal(true);
//   };

//   const handleToggleModule = async (materialId: string, currentStatus: boolean, e: React.MouseEvent) => {
//     e.stopPropagation();
//     setTogglingModuleId(materialId);
//     try {
//       const response = await apiClient.patch(
//         `/v1/study-materials/${materialId}/toggle-module`,
//         { is_course_module_active: !currentStatus }
//       );
//       if (response.data.success) {
//         setChapterMaterials(prev => {
//           const newState = { ...prev };
//           Object.keys(newState).forEach(chapterId => {
//             newState[chapterId] = newState[chapterId].map(material =>
//               material.id === materialId
//                 ? { ...material, is_course_module_active: !currentStatus }
//                 : material
//             );
//           });
//           return newState;
//         });
//         toast.current?.show({ severity: 'success', summary: 'Success', detail: `Study material ${!currentStatus ? 'added to' : 'removed from'} course modules`, life: 3000 });
//       } else {
//         toast.current?.show({ severity: 'error', summary: 'Error', detail: response.data.error || 'Failed to update module status', life: 4000 });
//       }
//     } catch (err: any) {
//       toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to update module status', life: 4000 });
//     } finally { setTogglingModuleId(null); }
//   };

//   const toggleModuleExpansion = (moduleId: string) => {
//     setExpandedModules(prev => {
//       const newSet = new Set(prev);
//       newSet.has(moduleId) ? newSet.delete(moduleId) : newSet.add(moduleId);
//       return newSet;
//     });
//   };

//   const toggleChapterExpansion = async (chapterId: string) => {
//     const isCurrentlyExpanded = expandedChapters.has(chapterId);
//     setExpandedChapters(prev => {
//       const newSet = new Set(prev);
//       newSet.has(chapterId) ? newSet.delete(chapterId) : newSet.add(chapterId);
//       return newSet;
//     });
//     if (!isCurrentlyExpanded && !chapterMaterials[chapterId]) {
//       await fetchChapterMaterials(chapterId);
//     }
//   };

//   const fetchChapterMaterials = async (chapterId: string) => {
//     if (loadingChapterMaterials.has(chapterId)) return;
//     setLoadingChapterMaterials(prev => { const s = new Set(prev); s.add(chapterId); return s; });
//     try {
//       const response = await apiClient.get(`/v1/study-materials/chapter/${chapterId}?status=active`);
//       if (response.data.success) {
//         setChapterMaterials(prev => ({ ...prev, [chapterId]: response.data.data || [] }));
//       }
//       console.log('test',response.data)
//     } catch (err: any) {
//       toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load study materials', life: 3000 });
//     } finally {
//       setLoadingChapterMaterials(prev => { const s = new Set(prev); s.delete(chapterId); return s; });
//     }
//   };

//   const getTotalMaterialsCount = () =>
//     modules.reduce((total, module) =>
//       total + module.chapters.reduce((ct, chapter) => ct + (chapter.materials_count || 0), 0), 0
//     );

//   const formatDate = (dateString: string) => {
//     try {
//       return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
//     } catch { return dateString; }
//   };

//   if (profileLoading) {
//     return (
//       <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '12px' }}>
//         <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
//         <p style={{ color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading profile data…</p>
//       </div>
//     );
//   }

//   if (!profile?.institute_id) {
//     return (
//       <div style={{ padding: '20px' }}>
//         <Message severity="warn" text="Unable to load profile data. Please refresh the page." style={{ fontSize: '0.88rem' }} />
//       </div>
//     );
//   }

//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
//       <Toast ref={toast} />

//       {/* ── Course Filter Card ────────────────────────────────────────────── */}
//       <div style={{
//         background: C.white,
//         borderRadius: '14px',
//         padding: '14px 18px',
//         boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
//         border: `1px solid ${C.border}`,
//       }}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
//           <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
//           <span style={{ fontSize: '0.78rem', fontWeight: '700', color: C.navy, letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
//             <i className="pi pi-filter" style={{ fontSize: '0.75rem' }} />
//             Select Course
//           </span>
//           {selectedCourseId && (
//             <button
//               onClick={() => setSelectedCourseId('')}
//               style={{
//                 marginLeft: 'auto', padding: '5px 10px',
//                 background: 'transparent', border: `1px solid ${C.border}`,
//                 borderRadius: '7px', color: C.sub,
//                 fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
//                 transition: 'all 0.15s ease', display: 'flex', alignItems: 'center', gap: '5px',
//               }}
//               onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EEF7'; e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.navy; }}
//               onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.sub; }}
//             >
//               <i className="pi pi-times" style={{ fontSize: '0.68rem' }} />
//               Clear
//             </button>
//           )}
//         </div>
//         <CourseChips
//           selectedCourseId={selectedCourseId}
//           onCourseSelect={handleCourseSelect}
//           showSearch={false}
//         />
//       </div>

//       {/* ── Error ─────────────────────────────────────────────────────────── */}
//       {error && <Message severity="error" text={error} style={{ fontSize: '0.88rem' }} />}

//       {/* ── Empty: no course selected ─────────────────────────────────────── */}
//       {!selectedCourseId ? (
//         <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
//           <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
//             <i className="pi pi-book" style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
//           </div>
//           <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>Select a Course</h3>
//           <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>Click on a course above to view its study materials</p>
//         </div>

//       ) : loading ? (
//         <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}` }}>
//           <div style={{ textAlign: 'center' }}>
//             <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" animationDuration=".8s" />
//             <p style={{ marginTop: '14px', color: C.navy, fontSize: '0.9rem', fontWeight: '500' }}>Loading study materials…</p>
//           </div>
//         </div>

//       ) : modules.length === 0 ? (
//         <div style={{ padding: '60px 20px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `2px dashed ${C.border}` }}>
//           <div style={{ width: '48px', height: '48px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
//             <i className="pi pi-book" style={{ fontSize: '1.4rem', color: C.navy, opacity: 0.3 }} />
//           </div>
//           <h3 style={{ margin: '0 0 6px 0', color: C.navy, fontSize: '0.95rem', fontWeight: '700' }}>No Study Materials Found</h3>
//           <p style={{ margin: 0, color: C.muted, fontSize: '0.83rem' }}>This course has no study materials yet</p>
//         </div>

//       ) : (
//         /* ── Modules accordion ─────────────────────────────────────────── */
//         <div style={{ background: C.white, borderRadius: '14px', padding: '16px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>

//           {/* Header */}
//           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
//             <div style={{ width: '3px', height: '16px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '3px' }} />
//             <span style={{ fontSize: '0.82rem', fontWeight: '700', color: C.navy }}>Course Structure</span>
//             <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: C.muted, fontWeight: '500' }}>
//               {modules.length} module{modules.length !== 1 ? 's' : ''} · {getTotalMaterialsCount()} material{getTotalMaterialsCount() !== 1 ? 's' : ''}
//             </span>
//           </div>

//           {modules.map((module) => {
//             const isModuleExpanded = expandedModules.has(module.id);

//             return (
//               <div key={module.id} style={{ marginBottom: '10px' }}>

//                 {/* ── Module Header ───────────────────────────────────── */}
//                 <div
//                   onClick={() => toggleModuleExpansion(module.id)}
//                   style={{
//                     padding: '12px 16px',
//                     background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
//                     borderRadius: isModuleExpanded ? '10px 10px 0 0' : '10px',
//                     cursor: 'pointer',
//                     transition: 'all 0.2s ease',
//                     position: 'relative', overflow: 'hidden',
//                     boxShadow: '0 2px 6px rgba(14,34,93,0.18)',
//                   }}
//                   onMouseEnter={(e) => {
//                     e.currentTarget.style.transform = 'translateY(-1px)';
//                     e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.26)';
//                   }}
//                   onMouseLeave={(e) => {
//                     e.currentTarget.style.transform = 'translateY(0)';
//                     e.currentTarget.style.boxShadow = '0 2px 6px rgba(14,34,93,0.18)';
//                   }}
//                 >
//                   <div style={{ position: 'absolute', top: '-16px', right: '-16px', width: '70px', height: '70px', background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(14px)', pointerEvents: 'none' }} />

//                   <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
//                     {/* Order badge */}
//                     <div style={{
//                       width: '32px', height: '32px', borderRadius: '8px',
//                       background: 'rgba(251,178,31,0.2)', border: '1px solid rgba(251,178,31,0.35)',
//                       display: 'flex', alignItems: 'center', justifyContent: 'center',
//                       fontSize: '0.82rem', fontWeight: '700', color: C.gold, flexShrink: 0,
//                     }}>
//                       {module.order}
//                     </div>

//                     <div style={{ flex: 1 }}>
//                       <h3 style={{ margin: '0 0 2px 0', fontSize: '0.88rem', fontWeight: '700', color: C.white }}>
//                         {module.title}
//                       </h3>
//                       {module.description && (
//                         <p style={{ margin: 0, fontSize: '0.73rem', color: 'rgba(255,255,255,0.55)', lineHeight: '1.4' }}>
//                           {module.description}
//                         </p>
//                       )}
//                     </div>

//                     <span style={{ fontSize: '0.68rem', color: C.gold, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
//                       <i className="pi pi-book" style={{ fontSize: '0.62rem' }} />
//                       {module.chapters_count} {module.chapters_count === 1 ? 'Chapter' : 'Chapters'}
//                     </span>

//                     <i className={`pi ${isModuleExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
//                       style={{ color: C.gold, fontSize: '0.8rem', flexShrink: 0 }}
//                     />
//                   </div>
//                 </div>

//                 {/* ── Chapters ────────────────────────────────────────── */}
//                 {isModuleExpanded && (
//                   <div style={{
//                     border: `1px solid ${C.border}`, borderTop: 'none',
//                     borderRadius: '0 0 10px 10px', padding: '12px',
//                     background: 'rgba(14,34,93,0.015)',
//                   }}>
//                     {module.chapters.map((chapter) => {
//                       const isChapterExpanded = expandedChapters.has(chapter.id);

//                       return (
//                         <div key={chapter.id} style={{ marginBottom: '8px' }}>

//                           {/* Chapter Header */}
//                           <div
//                             onClick={() => toggleChapterExpansion(chapter.id)}
//                             style={{
//                               padding: '10px 14px',
//                               background: isChapterExpanded ? '#E8EEF7' : C.white,
//                               border: `1px solid ${isChapterExpanded ? 'rgba(14,34,93,0.2)' : C.border}`,
//                               borderRadius: isChapterExpanded ? '8px 8px 0 0' : '8px',
//                               cursor: 'pointer', transition: 'all 0.15s ease',
//                             }}
//                             onMouseEnter={(e) => {
//                               if (!isChapterExpanded) {
//                                 e.currentTarget.style.background = '#F5F7FA';
//                                 e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
//                               }
//                             }}
//                             onMouseLeave={(e) => {
//                               if (!isChapterExpanded) {
//                                 e.currentTarget.style.background = C.white;
//                                 e.currentTarget.style.borderColor = C.border;
//                               }
//                             }}
//                           >
//                             <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
//                               {/* Chapter order badge */}
//                               <div style={{
//                                 width: '28px', height: '28px', borderRadius: '7px',
//                                 background: isChapterExpanded ? 'rgba(14,34,93,0.12)' : '#F5F7FA',
//                                 border: `1px solid ${isChapterExpanded ? 'rgba(14,34,93,0.2)' : C.border}`,
//                                 display: 'flex', alignItems: 'center', justifyContent: 'center',
//                                 fontSize: '0.68rem', fontWeight: '700', color: C.navy, flexShrink: 0,
//                               }}>
//                                 {module.order}.{chapter.order}
//                               </div>

//                               <div style={{ flex: 1 }}>
//                                 <h4 style={{ margin: '0 0 1px 0', fontSize: '0.83rem', fontWeight: '600', color: C.navy }}>
//                                   {chapter.title}
//                                 </h4>
//                                 {chapter.description && (
//                                   <p style={{ margin: 0, fontSize: '0.71rem', color: C.sub }}>
//                                     {chapter.description}
//                                   </p>
//                                 )}
//                               </div>

//                               <span style={{ fontSize: '0.68rem', color: C.muted, display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
//                                 <i className="pi pi-file" style={{ fontSize: '0.6rem' }} />
//                                 {chapter.materials_count} {chapter.materials_count === 1 ? 'Material' : 'Materials'}
//                               </span>

//                               <i className={`pi ${isChapterExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
//                                 style={{ color: C.navy, fontSize: '0.75rem', opacity: 0.5, flexShrink: 0 }}
//                               />
//                             </div>
//                           </div>

//                           {/* ── Study Materials ──────────────────────── */}
//                           {isChapterExpanded && (
//                             <div style={{
//                               border: `1px solid rgba(14,34,93,0.12)`, borderTop: 'none',
//                               borderRadius: '0 0 8px 8px', padding: '10px',
//                               background: C.white,
//                             }}>
//                               {loadingChapterMaterials.has(chapter.id) ? (
//                                 <div style={{ padding: '20px', textAlign: 'center' }}>
//                                   <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" animationDuration=".8s" />
//                                   <p style={{ margin: '8px 0 0 0', fontSize: '0.78rem', color: C.muted }}>Loading materials…</p>
//                                 </div>
//                               ) : chapterMaterials[chapter.id]?.length > 0 ? (
//                                 chapterMaterials[chapter.id].map((material) => (
//                                   <div
//                                     key={material.id}
//                                     onClick={() => handleViewDetails(material)}
//                                     style={{
//                                       padding: '12px 14px',
//                                       background: C.white,
//                                       border: `1px solid ${C.border}`,
//                                       borderRadius: '9px',
//                                       marginBottom: '7px',
//                                       cursor: 'pointer',
//                                       transition: 'all 0.15s ease',
//                                     }}
//                                     onMouseEnter={(e) => {
//                                       e.currentTarget.style.transform = 'translateX(3px)';
//                                       e.currentTarget.style.borderColor = C.goldBorder;
//                                       e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.07)';
//                                     }}
//                                     onMouseLeave={(e) => {
//                                       e.currentTarget.style.transform = 'translateX(0)';
//                                       e.currentTarget.style.borderColor = C.border;
//                                       e.currentTarget.style.boxShadow = 'none';
//                                     }}
//                                   >
//                                     <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>

//                                       {/* Material order badge */}
//                                       <div style={{
//                                         width: '30px', height: '30px', borderRadius: '8px',
//                                         background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
//                                         display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
//                                         boxShadow: '0 2px 6px rgba(14,34,93,0.18)',
//                                         fontSize: '0.55rem', fontWeight: '700', color: C.gold,
//                                         letterSpacing: '-0.3px', lineHeight: 1,
//                                       }}>
//                                         {module.order}.{chapter.order}.{material.order ?? '?'}
//                                       </div>

//                                       <div style={{ flex: 1, minWidth: 0 }}>
//                                         <h5 style={{ margin: '0 0 3px 0', fontSize: '0.83rem', fontWeight: '600', color: C.navy }}>
//                                           {material.title}
//                                         </h5>
//                                         <p style={{
//                                           margin: '0 0 7px 0', fontSize: '0.75rem', color: C.sub, lineHeight: '1.45',
//                                           display: '-webkit-box', WebkitLineClamp: 2,
//                                           WebkitBoxOrient: 'vertical', overflow: 'hidden',
//                                         }}>
//                                           {material.body}
//                                         </p>

//                                         {/* Meta row */}
//                                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.67rem', color: C.muted }}>
//                                           <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
//                                             <i className="pi pi-calendar" style={{ fontSize: '0.6rem' }} />
//                                             {formatDate(material.created_at)}
//                                           </span>
//                                           {material.files.length > 0 && (
//                                             <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: C.navy, fontWeight: '600' }}>
//                                               <i className="pi pi-paperclip" style={{ fontSize: '0.6rem' }} />
//                                               {material.files.length} {material.files.length === 1 ? 'file' : 'files'}
//                                             </span>
//                                           )}
//                                           {material.video_links.length > 0 && (
//                                             <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#b8860b', fontWeight: '600' }}>
//                                               <i className="pi pi-video" style={{ fontSize: '0.6rem' }} />
//                                               {material.video_links.length} {material.video_links.length === 1 ? 'video' : 'videos'}
//                                             </span>
//                                           )}
//                                         </div>
//                                       </div>

//                                       {/* Toggle module button */}
//                                       <button
//                                         onClick={(e) => handleToggleModule(material.id, material.is_course_module_active ?? false, e)}
//                                         disabled={togglingModuleId === material.id}
//                                         title={material.is_course_module_active ? 'Remove from Course Modules' : 'Add to Course Modules'}
//                                         style={{
//                                           padding: '5px 10px',
//                                           background: material.is_course_module_active ? C.greenDim : 'rgba(14,34,93,0.06)',
//                                           color: material.is_course_module_active ? C.green : C.navy,
//                                           border: `1px solid ${material.is_course_module_active ? C.greenBorder : 'rgba(14,34,93,0.18)'}`,
//                                           borderRadius: '7px',
//                                           cursor: togglingModuleId === material.id ? 'not-allowed' : 'pointer',
//                                           fontSize: '0.68rem', fontWeight: '700',
//                                           transition: 'all 0.15s ease',
//                                           display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0,
//                                           opacity: togglingModuleId === material.id ? 0.6 : 1,
//                                         }}
//                                         onMouseEnter={(e) => {
//                                           if (togglingModuleId !== material.id) e.currentTarget.style.transform = 'scale(1.03)';
//                                         }}
//                                         onMouseLeave={(e) => {
//                                           e.currentTarget.style.transform = 'scale(1)';
//                                         }}
//                                       >
//                                         {togglingModuleId === material.id ? (
//                                           <>
//                                             <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.62rem' }} />
//                                             Updating…
//                                           </>
//                                         ) : (
//                                           <>
//                                             <i className={`pi ${material.is_course_module_active ? 'pi-check-circle' : 'pi-circle'}`} style={{ fontSize: '0.62rem' }} />
//                                             {material.is_course_module_active ? 'Module' : 'Add'}
//                                           </>
//                                         )}
//                                       </button>
//                                     </div>
//                                   </div>
//                                 ))
//                               ) : (
//                                 <div style={{ padding: '20px', textAlign: 'center' }}>
//                                   <div style={{ width: '38px', height: '38px', background: 'rgba(14,34,93,0.05)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
//                                     <i className="pi pi-inbox" style={{ fontSize: '1rem', color: C.navy, opacity: 0.25 }} />
//                                   </div>
//                                   <p style={{ margin: 0, fontSize: '0.78rem', color: C.muted }}>No study materials in this chapter</p>
//                                 </div>
//                               )}
//                             </div>
//                           )}
//                         </div>
//                       );
//                     })}
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>
//       )}

//       {/* ── Details Modal ─────────────────────────────────────────────────── */}
//       <ViewStudyMaterialModal
//         visible={showDetailsModal}
//         material={selectedMaterial}
//         onHide={() => setShowDetailsModal(false)}
//       />
//     </div>
//   );
// };

// export default StudyMaterials;
